import type { ColumnDef, Table as ITable } from "@tanstack/vue-table";
import type { ActionButtonOption } from "./ActionButtons";
import type { ServerTable } from "./composables/server-table";
import type { ClientTable } from "./composables/table";
import type { FacetedFilter } from "./FacetedFilters";
import type { SelectedActionButtonOption } from "./SelectedActions";
import { FlexRender } from "@tanstack/vue-table";
import { computed, defineComponent, inject, provide, watch } from "vue";
import Spinner from "@/components/Spinner";
import { useCompoundSlots } from "@/composables/compound-slots";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Table, { tableCtx, tableProps } from "../../primitives/table/Table";
import IconButton from "../icon-button/IconButton";
import Icon from "../icon/Icon";
import InputNumber from "../input-number/InputNumber";
import ScrollArea, { adaptAreaScrollDisplay } from "../scroll-area/ScrollArea";
import Select from "../select/Select";
import ActionButtons from "./ActionButtons";
import FacetedFilters from "./FacetedFilters";
import GlobalFilter from "./GlobalFilter";

export const injectTable = <T extends any>(
  fallback?: ClientTable<T>
): ClientTable<T> | ServerTable<any> => {
  const table = inject("table", fallback);
  if (!table) {
    throw new Error("Table not found");
  }

  return table as ClientTable<T>;
};

const Root = defineComponent({
  name: "DataTableRoot",
  props: {
    table: p.$object<ITable<any> | ServerTable<any>>(),
  },
  setup(props, { slots }) {
    provide("table", props.table);

    return () => <div>{slots.default?.()}</div>;
  },
});

type SlotKey = "$loading" | "$empty";

const tableContentSlots = useCompoundSlots<SlotKey>();

const Content = defineComponent({
  name: "DataTable",
  props: {
    ...tableProps,
    table: p.object<ClientTable<any>>(),
    noItemsMessage: p.string(),
  },

  setup(props, { slots }) {
    const table = injectTable(props.table);
    const { getSlot } = tableContentSlots.provide();
    tableCtx.provide(table);

    const { loading } = table.getState();

    function tableClassByMeta(meta: ColumnDef<any>["meta"]) {
      const classes = [];

      if (meta && "freeze" in meta) {
        classes.push("sticky bg-inherit z-2");

        if (meta?.freeze === "left") {
          classes.push("left-0");
        }

        if (meta?.freeze === "right") {
          classes.push("right-0");
        }
      }

      if (meta && "class" in meta) {
        classes.push(meta.class);
      }

      return classes.join(" ");
    }

    function getAlignmentClass(align?: "left" | "center" | "right") {
      switch (align) {
        case "right":
          return "text-right";
        case "center":
          return "text-center";
        default:
          return "";
      }
    }

    function getHeaderAlignmentClass(align?: "left" | "center" | "right") {
      switch (align) {
        case "right":
          return "flex justify-end";
        case "center":
          return "flex justify-center";
        default:
          return "";
      }
    }

    return () => (
      <ScrollArea
        orientation="horizontal"
        type="hover"
        ref={adaptAreaScrollDisplay}
        scrollbarSize="sm"
      >
        <Table.Root
          class="relative w-full"
          {...props}
          style={{
            width: table.getCenterTotalSize(),
          }}
        >
          <Table.Header>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.HeadRow key={headerGroup.id} class="bg-card">
                {headerGroup.headers
                  .filter((c) => {
                    //@ts-ignore
                    return !c.column.columnDef.meta?.hide;
                  })
                  .map((header) => {
                    const meta = header.column.columnDef.meta;

                    return (
                      <Table.HeaderCell
                        key={header.id}
                        style={{
                          width: header.getSize(),
                        }}
                        data-sorted={!!header.column.getIsSorted()}
                        class={cn(
                          tableClassByMeta(meta),
                          "bg-surface-4 sorted:bg-surface-2",
                          "border-gray-3"
                        )}
                      >
                        {header.isPlaceholder ? null : meta?.align ? (
                          <div class={getHeaderAlignmentClass(meta.align)}>
                            <FlexRender
                              render={header.column.columnDef.header}
                              props={header.getContext()}
                            />
                          </div>
                        ) : (
                          <FlexRender
                            render={header.column.columnDef.header}
                            props={header.getContext()}
                          />
                        )}
                      </Table.HeaderCell>
                    );
                  })}
              </Table.HeadRow>
            ))}
          </Table.Header>
          <Table.Body>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row, index) => {
                return (
                  <Table.Row
                    key={row.id}
                    onClick={
                      table.onRowClicked ? () => table.onRowClicked?.(row.original) : undefined
                    }
                  >
                    {row
                      .getVisibleCells()
                      .filter((c) => {
                        //@ts-ignore
                        return !c.column.columnDef.meta?.hide;
                      })
                      .map((cell) => {
                        const size = cell.column.getSize();
                        const meta = cell.column.columnDef.meta;
                        const isOdd = index % 2 === 1;

                        return (
                          <>
                            <Table.Cell
                              key={cell.id}
                              style={{
                                width: `${size}px`,
                              }}
                              data-sorted={!!cell.column.getIsSorted()}
                              data-selected={!!cell.row.getIsSelected()}
                              class={cn(
                                "relative",
                                tableClassByMeta(meta),
                                getAlignmentClass(meta?.align),
                                !(isOdd && props.striped)
                                  ? "bg-surface-4 sorted:bg-surface-2 selected:bg-accent-2A"
                                  : "bg-surface-3 sorted:bg-surface-1 selected:bg-accent-1A"
                              )}
                            >
                              <FlexRender
                                render={cell.column.columnDef.cell}
                                props={cell.getContext()}
                              />
                            </Table.Cell>
                          </>
                        );
                      })}
                  </Table.Row>
                );
              })
            ) : (
              <Table.Row>
                <Table.Cell
                  class="p-0"
                  // @ts-ignore
                  colSpan={table.getAllColumns().length}
                >
                  {getSlot("$empty") ?? (
                    <div class="center w-full space-x-1 bg-surface-3 py-4 text-foreground/80">
                      <Icon icon="lucide:inbox" class="h-5 w-5" />
                      <span>{props.noItemsMessage ?? $t("table.no_items_found")}</span>
                    </div>
                  )}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
          {getSlot("$loading") ?? (
            <div v-show={loading.value} class={"center absolute inset-0 z-3 bg-card/50"}>
              {slots.default?.() ?? <Spinner class="h-6 w-6 text-brand-9" />}
            </div>
          )}
        </Table.Root>
      </ScrollArea>
    );
  },
});

const Loading = defineComponent({
  name: "DataTableLoading",
  props: {
    loading: p.boolean(null),
  },
  setup(props, { attrs, slots }) {
    const table = injectTable();
    const { createSlot } = tableContentSlots.inject();

    const loading = computed(() => {
      if (props.loading != null) return props.loading;
      return table.getState().loading.value;
    });

    createSlot("$loading", () => (
      <div
        v-show={loading.value}
        class={cn("center absolute inset-0 bg-card/50", attrs.class ?? "")}
      >
        {slots.default?.() ?? <Spinner class="h-6 w-6 text-brand-9" />}
      </div>
    ));

    return () => null;
  },
});

const Empty = defineComponent({
  name: "DataTableEmpty",
  setup(_, { attrs, slots }) {
    const { createSlot } = tableContentSlots.inject();

    createSlot("$empty", () => (
      <div class={cn("center w-full space-x-1 bg-surface-3 py-4", attrs.class ?? "")}>
        {slots.default?.()}
      </div>
    ));

    return () => null;
  },
});

const Pagination = defineComponent({
  name: "DataTablePagination",
  props: {
    table: p.object<ClientTable<any>>(),
    pageOptions: p.array<number[]>([5, 10, 25, 50, 100]),
    hideIfUneccessary: p.boolean(),
    compact: p.boolean(),
  },
  setup(props) {
    const table = injectTable(props.table);

    const pageSize = computed({
      get: () => table.getState().pagination.pageSize,
      set: (value) => table.setPageSize(value),
    });

    const pageIndex = computed({
      get: () => table.getState().pagination.pageIndex + 1,
      set: (value) => table.setPageIndex(value - 1),
    });

    // A numeric page count only exists where a total exists. Cursor mode has
    // none by construction: navigation follows stored cursors and the current
    // position is shown as plain text.
    const pageCount = computed<number | null>(() => {
      const { paging } = table.getState();
      switch (paging.mode) {
        case "client":
          return table.getPageCount() || 1;
        case "offset":
          // No "|| 1" here: a zero total yields ZERO pages so the nav hides,
          // matching the pre-cursor behavior for legacy server tables.
          return Math.ceil(paging.total.value / pageSize.value);
        case "cursor":
          return null;
      }
    });

    const canPreviousPage = computed<boolean>(() => {
      const { paging } = table.getState();
      switch (paging.mode) {
        case "client":
          return !table.getCanPreviousPage();
        case "offset":
          return pageIndex.value <= 1;
        case "cursor":
          return table.getState().pagination.pageIndex === 0;
      }
    });

    const canNextPage = computed<boolean>(() => {
      const { paging } = table.getState();
      switch (paging.mode) {
        case "client":
          return !table.getCanNextPage();
        case "offset":
          return pageIndex.value >= (pageCount.value ?? 1);
        case "cursor":
          // Gate forward movement on a KNOWN next key, never on a count.
          return table.getState().pagination.pageIndex >= paging.knownPage.value;
      }
    });

    interface NavButton {
      id: "first" | "previous" | "next" | "last";
      icon: string;
      handler: () => void;
      disabled: boolean;
      label: string;
    }

    // Named buttons so consumers never depend on array position.
    const buttons = computed<NavButton[]>(() => {
      const previousButton: NavButton = {
        id: "previous",
        icon: "lucide:chevron-left",
        handler: () => table.previousPage(),
        disabled: canPreviousPage.value,
        label: $t("table.previous_page"),
      };
      const nextButton: NavButton = {
        id: "next",
        icon: "lucide:chevron-right",
        handler: () => table.nextPage(),
        disabled: canNextPage.value,
        label: $t("table.next_page"),
      };

      if (props.compact || table.getState().paging.mode === "cursor") {
        return [previousButton, nextButton];
      }

      const firstButton: NavButton = {
        id: "first",
        icon: "lucide:chevrons-left",
        handler: () => table.setPageIndex(0),
        disabled: canPreviousPage.value,
        label: $t("table.first_page"),
      };
      const lastButton: NavButton = {
        id: "last",
        icon: "lucide:chevrons-right",
        handler: () => table.setPageIndex((pageCount.value ?? 1) - 1),
        disabled: canNextPage.value,
        label: $t("table.last_page"),
      };

      return [firstButton, previousButton, nextButton, lastButton];
    });

    // Footer total per mode. In cursor mode this is FOOTER TEXT ONLY (and
    // null whenever the server could not compute it); it never feeds
    // navigation above.
    const total = computed<number | null>(() => {
      const { paging } = table.getState();
      switch (paging.mode) {
        case "client":
          return table.getFilteredRowModel().rows.length;
        case "offset":
          return paging.total.value;
        case "cursor":
          return paging.exactTotal.value;
      }
    });

    const pageSizeOption = computed({
      get: () => String(pageSize.value),
      set: (val) => (pageSize.value = Number(val)),
    });

    const pageSizeOptions = computed(() => {
      return props.pageOptions.map((value) => ({
        value: String(value),
        label: `${value} / ${$t("table.page")}`,
      }));
    });

    const selectedRowsCount = computed(() => table.getSelectedRowModel().rows.length);

    const calculateInputSizeByMaxValue = (maxValue: number) => {
      const size = maxValue.toString().length;
      return Math.max(size + 2, 4);
    };

    // Reset page index when the page count shrinks (numeric modes only -
    // a cursor table has no count to shrink).
    watch(pageCount, (current, previous) => {
      if (typeof current === "number" && typeof previous === "number" && current < previous) {
        table.setPageIndex(0);
      }
    });

    const resultsWindow = computed(() => {
      const rowsOnPage = table.getRowModel().rows.length;
      if (!rowsOnPage) {
        return null;
      }

      const zeroBasedIndex = table.getState().pagination.pageIndex;
      const start = zeroBasedIndex * pageSize.value + 1;
      const { paging } = table.getState();

      if (paging.mode === "cursor") {
        // The live window comes from the served rows themselves - correct even
        // without any knowledge of an absolute total.
        return { start, end: start + rowsOnPage - 1 };
      }

      return {
        start,
        end: Math.min(zeroBasedIndex * pageSize.value + pageSize.value, total.value ?? start),
      };
    });

    const shouldHide = computed(() => {
      if (!props.hideIfUneccessary) {
        return false;
      }

      const { paging } = table.getState();
      const knownTotal = paging.mode === "cursor" ? paging.exactTotal.value : total.value;

      // Without an exact total there is nothing to compare against: stay
      // visible rather than hiding pagination based on guesses.
      return knownTotal != null && knownTotal <= pageSize.value;
    });

    return () =>
      !shouldHide.value && (
        <div class="center flex-wrap justify-between gap-2 border-t px-3 pb-1.5 pt-2">
          <div>
            {selectedRowsCount.value > 0 ? (
              <span>
                <strong>{selectedRowsCount.value.toLocaleString()}</strong>
                {total.value != null && (
                  <>
                    {" "}
                    {$t("table.of")} <strong>{total.value.toLocaleString()}</strong>{" "}
                  </>
                )}
                {$t("table.selected", {
                  count: selectedRowsCount.value,
                })}
              </span>
            ) : (
              <span>
                {$t("table.showing")}{" "}
                {resultsWindow.value ? (
                  <>
                    <strong>{resultsWindow.value.start.toLocaleString()}</strong> -{" "}
                    <strong>{resultsWindow.value.end.toLocaleString()}</strong>
                  </>
                ) : (
                  <strong>0</strong>
                )}{" "}
                {total.value != null && (
                  <>
                    {$t("table.of")} <strong>{total.value.toLocaleString()}</strong>{" "}
                    {total.value > 1 ? $t("table.results") : $t("table.result")}
                  </>
                )}
              </span>
            )}
          </div>
          <nav
            class="flex flex-wrap gap-2 sm:flex-nowrap"
            v-show={pageCount.value == null || pageCount.value > 0}
          >
            {!props.compact &&
              (table.getState().paging.mode === "cursor" ? (
                <div class="center gap-x-1 text-sm whitespace-nowrap">
                  <span>{$t("table.page")} </span>
                  <span class="mr-1 text-sm font-medium">{pageIndex.value}</span>
                </div>
              ) : (
                <div class="center gap-x-1 text-sm whitespace-nowrap">
                  <span>{$t("table.page")} </span>
                  <div
                    class="w-10 text-center"
                    style={{
                      width: `${calculateInputSizeByMaxValue(pageCount.value ?? 1)}ch`,
                    }}
                  >
                    <InputNumber
                      hideButtons
                      size="sm"
                      min={1}
                      max={pageCount.value ?? 1}
                      clearable={false}
                      v-model={pageIndex.value}
                    />
                  </div>
                  <span>
                    {$t("table.of")} {(pageCount.value ?? 1).toLocaleString()}
                  </span>
                </div>
              ))}
            {!props.compact && (
              <Select
                size="sm"
                class="max-w-32 text-nowrap"
                v-model={pageSizeOption.value}
                options={pageSizeOptions.value}
              />
            )}
            <div class="center space-x-2">
              {buttons.value.map(
                ({ id, icon, handler, disabled: disabledButton, label }) => (
                  <IconButton
                    key={id}
                    label={label}
                    size="sm"
                    variant="outline"
                    icon={icon}
                    onClick={handler}
                    disabled={disabledButton}
                  />
                )
              )}
            </div>
          </nav>
        </div>
      );
  },
});

const Toolbar = defineComponent({
  name: "DatatableToolbar",
  props: {
    table: p.object<ClientTable<any>>(),
    buttons: p.array<ActionButtonOption[]>([]),
    selectedButtons: p.array<SelectedActionButtonOption[]>([]),
    globalFilter: p.boolean(),
    filters: p.array<FacetedFilter[]>([]),
    focusShortcut: p.array<string[]>(["meta", "j"]),
  },
  setup(props, { slots }) {
    const table = injectTable(props.table);

    provide("table", table);
    const selectedItems = computed(() => {
      return table.getSelectedRowModel().rows.map((row) => row.original);
    });

    const selectedButtons = computed(() => {
      return props.selectedButtons.map((button) => {
        return {
          ...button,
          handler: () => button.handler(selectedItems.value),
        };
      });
    });

    return () => (
      <div class="border-b">
        <div class="center flex-wrap items-start justify-between gap-2 p-2">
          <div class="center grow basis-96 flex-wrap items-end justify-start gap-2">
            {props.globalFilter && <GlobalFilter focusShortcut={props.focusShortcut} />}
            {!!props.filters.length && <FacetedFilters filters={props.filters} />}
          </div>
          <div class="flex shrink-0 flex-wrap gap-2.5 py-0.5">
            {slots.$beforeButtons?.()}
            {!selectedItems.value.length && <ActionButtons table={table} buttons={props.buttons} />}
            {slots.$right?.()}
          </div>
        </div>
        {selectedItems.value.length > 0 && (
          <div class="mx-4 mb-3 flex items-center gap-3">
            <span class="text-sm text-muted-foreground">
              {selectedItems.value.length}{" "}
              {$t("items_selected", { count: selectedItems.value.length })}
            </span>
            <ActionButtons table={table} buttons={selectedButtons.value} />
          </div>
        )}
      </div>
    );
  },
});

export default {
  Root,
  Content,
  Pagination,
  Toolbar,
  Loading,
  Empty,
};
